import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';

import {
  getBlockStorage,
  getBlockNodeByInstanceId,
  isAiToolkitReady,
  isSelectionCrossesBlockBoundary,
} from '@/utils/editor-utils';

export type ActiveBlock = {
  id: string;
  name: string;
  contentRange: { from: number; to: number };
};

export const useLlmEditorScope = (editor: Editor | null) => {
  const [activeBlock, setActiveBlock] = useState<ActiveBlock | null>(null);

  const hasSelection = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!isAiToolkitReady(editor)) {
        return false;
      }

      try {
        const toolkit = getAiToolkit(editor);
        const suggestions = toolkit.getSuggestions();

        return !editor.state.selection.empty && !suggestions.length && !isSelectionCrossesBlockBoundary(editor);
      } catch {
        return false;
      }
    },
  });

  useEffect(() => {
    if (!editor) {
      setActiveBlock(null);
      return;
    }

    const handleActiveBlock = () => {
      const { selectedBlockInstanceId } = getBlockStorage(editor) || {};

      if (selectedBlockInstanceId) {
        const blockNode = getBlockNodeByInstanceId(editor, selectedBlockInstanceId);

        if (blockNode) {
          const node = blockNode.node;
          const next: ActiveBlock = {
            id: node.attrs.id,
            name: node.attrs.name,
            contentRange: {
              from: blockNode.startPos + 1,
              to: blockNode.startPos + 1 + node.content.size,
            },
          };
          // This runs on every transaction (~30/s while an agent streams into the block), so keep
          // the previous object when nothing changed to avoid re-rendering the prompt box per frame.
          setActiveBlock((previous) => (isSameActiveBlock(previous, next) ? previous : next));
          return;
        }
      }

      setActiveBlock(null);
    };

    editor.on('transaction', handleActiveBlock);
    editor.on('selectionUpdate', handleActiveBlock);
    handleActiveBlock();

    return () => {
      editor.off('selectionUpdate', handleActiveBlock);
      editor.off('transaction', handleActiveBlock);
    };
  }, [editor]);

  return { hasSelection: !!hasSelection, activeBlock };
};

const isSameActiveBlock = (a: ActiveBlock | null, b: ActiveBlock) =>
  !!a &&
  a.id === b.id &&
  a.name === b.name &&
  a.contentRange.from === b.contentRange.from &&
  a.contentRange.to === b.contentRange.to;
